/**
 * ZATCA Phase 2 — device onboarding + live status page (iter869).
 *
 * Talks to the modern `/zatca-p2/*` endpoints:
 *   GET  /zatca-p2/status            → device state (production_csid | compliance_csid | csr_generated | none)
 *   POST /zatca-p2/generate-csr      → generate keypair + CSR on server
 *   POST /zatca-p2/compliance-csid   → submit CSR + OTP to ZATCA, receive Compliance CSID
 *   POST /zatca-p2/run-compliance    → run the 6 compliance test invoices
 *   POST /zatca-p2/production-csid   → issue the final Production CSID
 *   GET  /zatca-p2/health-summary    → today/all-time submission counts + failed list
 *   POST /zatca-p2/retry-failed      → bulk-retry FAILED submissions
 *
 * Fallback: the legacy static paste-in form at `/zatca-phase2/config`
 * still exists in the backend for users who obtained credentials elsewhere.
 */
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Info, AlertTriangle, ExternalLink, RefreshCw, KeyRound, FileText, Zap, CheckCircle2, XCircle, Loader2, Mail, Send } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { SSoTCompanyInfoBanner } from '@/components/common/SSoTCompanyInfoBanner';
import { ZatcaSellerReadinessCard } from '@/components/settings/tabs/ZatcaSellerReadinessCard';
import { PurchaseInvoiceVerification } from '@/components/zatca/PurchaseInvoiceVerification';

const STATE_LABEL = {
  csr_generated: 'CSR Generated (awaiting Compliance CSID)',
  compliance_csid: 'Compliance CSID Issued (run compliance checks next)',
  compliance_passed: 'Compliance Passed (request Production CSID)',
  production_csid: 'Production CSID — LIVE',
};

export default function ZATCAPhase2Page() {
  const [status, setStatus] = useState(null);
  const [health, setHealth] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [scope, setScope] = useState('today');
  const [busy, setBusy] = useState('');
  const [csrForm, setCsrForm] = useState({
    environment: 'simulation',
    vat_number: '',
    organization_name: '',
    organization_unit: '',
    common_name: '',
    serial_solution: 'Smarto',
    serial_model: 'POS',
    serial_serial: '000001',
    invoice_type: '1100',
    location: 'Riyadh',
    industry: 'Retail',
  });
  const [otp, setOtp] = useState('');
  const [complianceResults, setComplianceResults] = useState(null);
  // iter873b — ZATCA daily digest recipients (Fatoora reconciliation team)
  const [digestRecipients, setDigestRecipients] = useState('');
  const [digestBusy, setDigestBusy] = useState('');
  const [digestLastResult, setDigestLastResult] = useState(null);

  const loadStatus = async () => {
    try {
      const r = await api.get('/zatca-p2/status');
      setStatus(r.data);
      const dev = r.data?.device || {};
      // STEP 3 — prefill the CSR form. Priority: saved EGS/device value → canonical
      // Branding (SSoT) fallback ONLY when the device field is blank. A value the
      // user has already typed (f.x) is never overwritten. Common Name / Location /
      // Industry / Serial are left to device/user values only (no Branding fallback).
      let brand = {};
      try { brand = (await api.get('/pdf-exports/branding')).data || {}; } catch { brand = {}; }
      // Legally registered name first (zatca_seller_name), else the company name.
      const legalName = (brand.zatca_seller_name || brand.company_name || '').trim();
      const brandVat = (brand.company_vat || '').trim();
      setCsrForm((f) => ({
        ...f,
        vat_number: f.vat_number || dev.vat_number || brandVat || '',
        organization_name: f.organization_name || dev.organization_name || legalName || '',
        common_name: f.common_name || dev.common_name || '',
        environment: dev.environment || f.environment,
      }));
      if (r.data?.device?.compliance_results) {
        setComplianceResults(r.data.device.compliance_results);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load status'); }
  };

  const loadHealth = async () => {
    try {
      const r = await api.get(`/zatca-p2/health-summary?scope=${scope}`);
      setHealth(r.data);
    } catch { /* health is optional */ }
  };

  const loadIntegrity = async () => {
    try {
      const r = await api.get(`/zatca-p2/hash-integrity?scope=${scope}`);
      setIntegrity(r.data);
    } catch { /* optional */ }
  };

  const loadDigestRecipients = async () => {
    try {
      const r = await api.get('/zatca-digest/recipients');
      setDigestRecipients((r.data?.recipients || []).join(', '));
    } catch { /* optional */ }
  };

  const saveDigestRecipients = async () => {
    setDigestBusy('save');
    try {
      const list = digestRecipients.split(',').map(s => s.trim()).filter(Boolean);
      const r = await api.put('/zatca-digest/recipients', { recipients: list });
      toast.success(`Saved — ${r.data.recipients.length} recipient${r.data.recipients.length === 1 ? '' : 's'}`);
      setDigestRecipients(r.data.recipients.join(', '));
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setDigestBusy(''); }
  };

  const sendDigestNow = async () => {
    setDigestBusy('send');
    setDigestLastResult(null);
    try {
      const r = await api.post('/zatca-digest/send-now');
      setDigestLastResult(r.data);
      if (r.data?.status === 'sent') toast.success(`Digest sent — ${r.data.submissions} submissions to ${(r.data.recipients || []).length} recipient(s)`);
      else if (r.data?.status === 'skipped') toast.warning(`Skipped: ${r.data.reason}`);
      else toast.error(`Send failed: ${r.data?.error || r.data?.status}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setDigestBusy(''); }
  };

  useEffect(() => { loadStatus(); loadDigestRecipients(); }, []);
  useEffect(() => { loadHealth(); loadIntegrity(); }, [scope, status]);

  const generateCsr = async () => {
    if (!csrForm.vat_number || csrForm.vat_number.length !== 15) {
      toast.error('VAT number must be 15 digits'); return;
    }
    if (!csrForm.organization_name) {
      toast.error('Organization name is required'); return;
    }
    setBusy('csr');
    try {
      const r = await api.post('/zatca-p2/generate-csr', csrForm);
      toast.success(r.data?.message || 'CSR generated');
      await loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'CSR generation failed'); }
    finally { setBusy(''); }
  };

  const requestComplianceCsid = async () => {
    if (!otp) { toast.error('Enter the OTP from the Fatoora portal'); return; }
    setBusy('ccsid');
    try {
      const r = await api.post('/zatca-p2/compliance-csid', { otp });
      toast.success(r.data?.message || 'Compliance CSID issued');
      setOtp('');
      await loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Compliance CSID failed'); }
    finally { setBusy(''); }
  };

  const runCompliance = async () => {
    setBusy('compliance');
    try {
      const r = await api.post('/zatca-p2/run-compliance');
      setComplianceResults(r.data?.results || []);
      if (r.data?.all_passed) toast.success('All 6 compliance checks passed');
      else toast.error('Some compliance checks failed — review below');
      await loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Compliance run failed'); }
    finally { setBusy(''); }
  };

  const requestProductionCsid = async () => {
    setBusy('pcsid');
    try {
      const r = await api.post('/zatca-p2/production-csid');
      toast.success(r.data?.message || 'Production CSID issued');
      await loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Production CSID failed'); }
    finally { setBusy(''); }
  };

  const resolveBlocked = async (action) => {
    const verb = action === 'accepted' ? 'Confirmed ACCEPTED' : 'Confirmed NOT accepted';
    if (!window.confirm(`${verb} by ZATCA.\n\nOnly proceed after you have independently verified this invoice (UUID shown) in the ZATCA portal. This cannot be undone.`)) return;
    setBusy(`resolve-${action}`);
    try {
      const r = await api.post('/zatca-p2/resolve-blocked', { action });
      toast.success(r.data?.message || 'EGS chain unblocked');
      await loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Resolution failed'); }
    finally { setBusy(''); }
  };

  const retryFailed = async () => {
    setBusy('retry');
    try {
      const r = await api.post('/zatca-p2/retry-failed');
      toast.success(`Queued ${r.data?.queued || 0} failed submissions for retry`);
      setTimeout(loadHealth, 3000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Retry failed'); }
    finally { setBusy(''); }
  };

  const dev = status?.device;
  const state = dev?.state || 'none';
  const isLive = state === 'production_csid';
  const canRequestCompliance = ['csr_generated', 'compliance_csid'].includes(state);
  const canRunCompliance = ['compliance_csid', 'compliance_passed'].includes(state);
  const canRequestProduction = ['compliance_passed', 'production_csid'].includes(state) && dev?.has_compliance_csid;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="zatca-phase2-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center shadow-lg">
            <ShieldCheck size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">ZATCA Phase 2 E-Invoicing</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Real-time submission of every POS invoice to ZATCA (B2C reporting / B2B clearance)</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => { loadStatus(); loadHealth(); }} className="ml-auto" data-testid="zatca-refresh">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </div>

        <SSoTCompanyInfoBanner scopeLabel="ZATCA seller identity" />
        <ZatcaSellerReadinessCard />
        <PurchaseInvoiceVerification />

        {status?.chain_blocked && (
          <Card className="border-2 border-rose-300 bg-rose-50" data-testid="zatca-chain-blocked-banner">
            <CardContent className="pt-4 space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-rose-700">ZATCA chain BLOCKED — unknown submission outcome</p>
                  <p className="text-rose-600/90 mt-0.5">A submission could not be confirmed, so the EGS invoice chain is paused to protect its integrity. New POS/invoice submissions are refused until you resolve this. Verify the invoice below in the ZATCA portal, then choose one action.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs bg-white rounded-lg border border-rose-200 p-3 font-mono" data-testid="zatca-chain-blocked-details">
                <div><span className="text-muted-foreground">Order/Ref: </span>{status.chain_blocked.order_ref || '—'}</div>
                <div><span className="text-muted-foreground">ICV: </span>{status.chain_blocked.icv ?? '—'}</div>
                <div className="col-span-2 break-all"><span className="text-muted-foreground">UUID: </span>{status.chain_blocked.uuid || '—'}</div>
                <div><span className="text-muted-foreground">Reason: </span>{status.chain_blocked.reason || '—'}</div>
                <div><span className="text-muted-foreground">At: </span>{status.chain_blocked.at ? new Date(status.chain_blocked.at).toLocaleString() : '—'}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={!!busy} onClick={() => resolveBlocked('accepted')} data-testid="zatca-resolve-accepted-btn">
                  {busy === 'resolve-accepted' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  <span className="ml-1.5">Confirmed Accepted by ZATCA</span>
                </Button>
                <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-100" disabled={!!busy} onClick={() => resolveBlocked('not_accepted')} data-testid="zatca-resolve-not-accepted-btn">
                  {busy === 'resolve-not_accepted' ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                  <span className="ml-1.5">Confirmed NOT Accepted</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* LIVE status banner (only when production_csid) */}
        {isLive && (
          <Card className="border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/10 dark:to-teal-900/10" data-testid="zatca-live-banner">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 size={24} className="text-emerald-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-bold text-emerald-900 dark:text-emerald-200 text-lg">Phase 2 LIVE — Production ({dev.environment})</p>
                  <p className="text-xs text-muted-foreground mt-1">Every POS invoice is being cryptographically signed and submitted to ZATCA in real time. B2C → Reporting API. B2B (buyer VAT present) → Clearance API.</p>
                  <div className="flex flex-wrap gap-2 mt-3 text-[11px]">
                    <Badge variant="outline" className="border-emerald-300 text-emerald-700">VAT {dev.vat_number}</Badge>
                    <Badge variant="outline" className="border-emerald-300 text-emerald-700">{dev.organization_name}</Badge>
                    <Badge variant="outline" className="border-emerald-300 text-emerald-700">CN: {dev.common_name}</Badge>
                    <Badge variant="outline" className="border-emerald-300 text-emerald-700">Invoice type {dev.invoice_type}</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Health summary — only when onboarded */}
        {isLive && health?.onboarded && (
          <Card data-testid="zatca-health-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><Zap size={14}/> Submission Health</CardTitle>
              <div className="flex items-center gap-2">
                <Select value={scope} onValueChange={setScope}>
                  <SelectTrigger className="w-32 h-8" data-testid="zatca-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="all">All time</SelectItem>
                  </SelectContent>
                </Select>
                {(health.counts?.FAILED || 0) > 0 && (
                  <Button size="sm" variant="outline" onClick={retryFailed} disabled={busy==='retry'} data-testid="zatca-retry-failed">
                    {busy==='retry' ? <Loader2 size={12} className="animate-spin mr-1"/> : <RefreshCw size={12} className="mr-1" />}
                    Retry all failed
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatTile testid="zatca-reported-tile" label="Reported (B2C)" value={health.counts?.REPORTED || 0} tone="emerald" />
                <StatTile testid="zatca-cleared-tile" label="Cleared (B2B)" value={health.counts?.CLEARED || 0} tone="teal" />
                <StatTile testid="zatca-pending-tile" label="Pending" value={health.counts?.PENDING || 0} tone="amber" />
                <StatTile testid="zatca-failed-tile" label="Failed" value={health.counts?.FAILED || 0} tone="rose" />
              </div>
              {health.failed?.length > 0 && (
                <div className="mt-4 border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-rose-50 dark:bg-rose-900/20 text-xs font-semibold text-rose-800 dark:text-rose-200">Failed submissions ({health.failed.length})</div>
                  <div className="max-h-60 overflow-y-auto divide-y">
                    {health.failed.map((f) => (
                      <div key={f.id} className="px-3 py-2 text-xs">
                        <div className="flex justify-between font-mono">
                          <span>{f.order_number || f.id.slice(0,8)}</span>
                          <span>{Number(f.total || 0).toFixed(2)} SAR</span>
                        </div>
                        {f.detail && <div className="text-rose-600 mt-1 truncate" title={f.detail}>{f.detail}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {health.total === 0 && <p className="text-xs text-muted-foreground mt-3">No invoices submitted in this window.</p>}
            </CardContent>
          </Card>
        )}

        {/* iter899 — KSA Hash Integrity audit. Verifies every submitted
            invoice has a well-formed SHA-256 hash (base64, 44 chars) + a
            monotonic ICV chain, per ZATCA e-invoicing spec. */}
        {isLive && integrity?.onboarded && (
          <Card data-testid="zatca-integrity-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck size={14} /> Hash &amp; Chain Integrity
                {integrity.total_checked > 0 && (
                  <Badge
                    className={`ml-1 text-[10px] ${integrity.integrity_score >= 99 ? 'bg-emerald-600' : integrity.integrity_score >= 90 ? 'bg-amber-500' : 'bg-rose-600'} text-white`}
                    data-testid="zatca-integrity-score"
                  >
                    {integrity.integrity_score}% valid
                  </Badge>
                )}
              </CardTitle>
              <Button size="sm" variant="outline" onClick={loadIntegrity} data-testid="zatca-integrity-refresh">
                <RefreshCw size={12} className="mr-1" /> Re-verify
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <StatTile testid="integrity-ok-tile" label="Hash OK" value={integrity.hash_ok || 0} tone="emerald" />
                <StatTile testid="integrity-missing-tile" label="Missing" value={integrity.hash_missing || 0} tone="rose" />
                <StatTile testid="integrity-malformed-tile" label="Malformed" value={integrity.hash_malformed || 0} tone="rose" />
                <StatTile testid="integrity-icv-gaps-tile" label="ICV Gaps" value={integrity.icv_gaps || 0} tone="amber" />
                <StatTile testid="integrity-chain-broken-tile" label="Chain Broken" value={integrity.chain_broken || 0} tone="amber" />
              </div>
              {integrity.failures?.length > 0 && (
                <div className="mt-4 border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 text-xs font-semibold text-amber-800 dark:text-amber-200">
                    Failing invoices ({integrity.failures.length}{integrity.failures.length === 100 ? '+' : ''})
                  </div>
                  <div className="max-h-60 overflow-y-auto divide-y" data-testid="zatca-integrity-failures">
                    {integrity.failures.map((f) => (
                      <div key={f.id} className="px-3 py-2 text-xs">
                        <div className="flex justify-between font-mono">
                          <span>{f.order_number || f.id.slice(0, 8)} <span className="text-muted-foreground ml-1">ICV {f.icv || '—'}</span></span>
                          <span>{Number(f.total || 0).toFixed(2)} SAR</span>
                        </div>
                        <div className="text-rose-600 mt-1 flex flex-wrap gap-1">
                          {f.errors.map((e) => (
                            <Badge key={e} variant="outline" className="text-[9px] border-rose-300 text-rose-700 py-0 px-1.5">{e}</Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {integrity.total_checked > 0 && integrity.failures?.length === 0 && (
                <p className="text-xs text-emerald-700 mt-3 flex items-center gap-1">
                  <CheckCircle2 size={12} /> All {integrity.total_checked} submitted invoices pass ZATCA hash + chain validation.
                </p>
              )}
              {integrity.total_checked === 0 && <p className="text-xs text-muted-foreground mt-3">No invoices to validate in this window.</p>}
            </CardContent>
          </Card>
        )}

        {/* iter873b — Daily Fatoora-reconciliation digest recipients. Only
            shown once the tenant is LIVE on Phase 2; hidden during onboarding. */}
        {isLive && (
          <Card data-testid="zatca-digest-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Mail size={14} /> Daily reconciliation digest
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Every morning at <b>06:00 UTC</b> we email a signed PDF listing yesterday&apos;s submitted invoices
                (Order # · UUID · Status · ICV · Total · Hash). A <b>weekly reconciliation report</b> is also sent
                every <b>Monday at 07:00 UTC</b> with 7-day totals, VAT sum, and per-branch failure rates.
                Leave blank to disable both.
              </p>
              <div>
                <Label className="text-xs">Recipients (comma-separated emails)</Label>
                <Input
                  value={digestRecipients}
                  onChange={(e) => setDigestRecipients(e.target.value)}
                  placeholder="fatoora@yourcompany.com, accounts@yourcompany.com"
                  data-testid="zatca-digest-recipients-input"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={saveDigestRecipients} disabled={digestBusy === 'save'} data-testid="zatca-digest-save-btn">
                  {digestBusy === 'save' ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
                  Save recipients
                </Button>
                <Button size="sm" variant="outline" onClick={sendDigestNow} disabled={digestBusy === 'send' || !digestRecipients.trim()} data-testid="zatca-digest-send-now-btn">
                  {digestBusy === 'send' ? <Loader2 size={12} className="animate-spin mr-1" /> : <Send size={12} className="mr-1" />}
                  Send daily test now
                </Button>
                <Button size="sm" variant="outline" onClick={async () => {
                  setDigestBusy('weekly'); setDigestLastResult(null);
                  try {
                    const r = await api.post('/zatca-digest/weekly-send-now');
                    setDigestLastResult(r.data);
                    if (r.data?.status === 'sent') toast.success(`Weekly report sent — ${r.data.submissions} submissions (VAT ${(r.data.total_vat || 0).toLocaleString()} SAR)`);
                    else if (r.data?.status === 'skipped') toast.warning(`Skipped: ${r.data.reason}`);
                    else toast.error(`Send failed: ${r.data?.error || r.data?.status}`);
                  } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
                  finally { setDigestBusy(''); }
                }} disabled={digestBusy === 'weekly' || !digestRecipients.trim()} data-testid="zatca-weekly-send-btn">
                  {digestBusy === 'weekly' ? <Loader2 size={12} className="animate-spin mr-1" /> : <Send size={12} className="mr-1" />}
                  Send weekly test
                </Button>
                {digestLastResult && (
                  <span className={`text-[11px] px-2 py-1 rounded ${
                    digestLastResult.status === 'sent' ? 'bg-emerald-50 text-emerald-700' :
                    digestLastResult.status === 'skipped' ? 'bg-amber-50 text-amber-700' :
                    'bg-rose-50 text-rose-700'
                  }`}>
                    {digestLastResult.status} · {digestLastResult.submissions ?? 0} submissions
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Onboarding wizard — hidden when LIVE (unless user forces re-onboarding via advanced) */}
        {!isLive && (
          <>
            <Card className="border-amber-300 bg-amber-50/50 dark:bg-amber-900/10">
              <CardContent className="pt-4 flex items-start gap-3">
                <AlertTriangle size={22} className="text-amber-600 shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-foreground">{STATE_LABEL[state] || 'Phase 2 not onboarded'}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">SDK: {status?.sdk_version || 'loading…'} · Java: {status?.java_available ? '✓ ready' : '✗ missing'}</p>
                  {dev?.last_message && <p className="text-[11px] text-muted-foreground mt-1 italic">Last: {dev.last_message}</p>}
                </div>
              </CardContent>
            </Card>

            {/* Step 1 — Generate CSR */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText size={14} /> Step 1 — Generate Certificate Signing Request (CSR)
                  {dev?.has_csr && <Badge variant="outline" className="ml-2 border-emerald-300 text-emerald-700 text-[10px]">✓ Done</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Environment</Label>
                    <Select value={csrForm.environment} onValueChange={(v) => setCsrForm({...csrForm, environment: v})}>
                      <SelectTrigger data-testid="zatca-env-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sandbox">Sandbox (developer portal)</SelectItem>
                        <SelectItem value="simulation">Simulation (pre-prod tests)</SelectItem>
                        <SelectItem value="production">Production (LIVE)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">VAT Number (15 digits) *</Label>
                    <Input value={csrForm.vat_number} onChange={(e) => setCsrForm({...csrForm, vat_number: e.target.value.replace(/\D/g,'').slice(0,15)})} placeholder="3XXXXXXXXX00003" data-testid="zatca-vat-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Organization Name *</Label>
                    <Input value={csrForm.organization_name} onChange={(e) => setCsrForm({...csrForm, organization_name: e.target.value})} data-testid="zatca-org-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Common Name (device label)</Label>
                    <Input value={csrForm.common_name} onChange={(e) => setCsrForm({...csrForm, common_name: e.target.value})} placeholder="Smarto-POS-01" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Location (City)</Label>
                    <Input value={csrForm.location} onChange={(e) => setCsrForm({...csrForm, location: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs">Industry</Label>
                    <Input value={csrForm.industry} onChange={(e) => setCsrForm({...csrForm, industry: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs">Serial (device #)</Label>
                    <Input value={csrForm.serial_serial} onChange={(e) => setCsrForm({...csrForm, serial_serial: e.target.value})} />
                  </div>
                </div>
                <Button onClick={generateCsr} disabled={busy==='csr'} className="bg-violet-500 hover:bg-violet-600 text-white" data-testid="zatca-gen-csr-btn">
                  {busy==='csr' ? <Loader2 size={14} className="animate-spin mr-1" /> : <KeyRound size={14} className="mr-1" />}
                  {dev?.has_csr ? 'Re-generate CSR' : 'Generate CSR'}
                </Button>
              </CardContent>
            </Card>

            {/* Step 2 — Compliance CSID (needs OTP) */}
            {dev?.has_csr && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    Step 2 — Compliance CSID (OTP from Fatoora)
                    {dev?.has_compliance_csid && <Badge variant="outline" className="ml-2 border-emerald-300 text-emerald-700 text-[10px]">✓ Done</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-xs text-muted-foreground">
                    Log in to <a href="https://fatoora.zatca.gov.sa" target="_blank" rel="noreferrer" className="text-info hover:underline inline-flex items-center gap-1">Fatoora portal <ExternalLink size={11}/></a> → Onboard New EGS → generate an OTP → paste it here.
                  </div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-xs">OTP (from Fatoora portal)</Label>
                      <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g,'').slice(0,8))} placeholder="12345678" className="font-mono" maxLength={8} data-testid="zatca-otp-input" disabled={!canRequestCompliance} />
                    </div>
                    <Button onClick={requestComplianceCsid} disabled={!canRequestCompliance || busy==='ccsid' || !otp} data-testid="zatca-ccsid-btn">
                      {busy==='ccsid' ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                      Request Compliance CSID
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 3 — Run compliance */}
            {dev?.has_compliance_csid && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    Step 3 — Run 6 Compliance Test Invoices
                    {state === 'compliance_passed' && <Badge variant="outline" className="ml-2 border-emerald-300 text-emerald-700 text-[10px]">✓ Passed</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-muted-foreground">ZATCA requires 6 test documents (standard/simplified × invoice/credit/debit) to be signed + submitted before it will issue a Production CSID.</p>
                  <Button onClick={runCompliance} disabled={!canRunCompliance || busy==='compliance'} data-testid="zatca-run-compliance-btn">
                    {busy==='compliance' ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                    Run compliance checks
                  </Button>
                  {complianceResults && (
                    <div className="border rounded-lg overflow-hidden mt-3">
                      <table className="w-full text-xs">
                        <thead className="bg-secondary/40"><tr>
                          <th className="text-left px-3 py-2">Doc</th>
                          <th className="text-left px-3 py-2">ICV</th>
                          <th className="text-left px-3 py-2">Status</th>
                          <th className="text-left px-3 py-2">Detail</th>
                        </tr></thead>
                        <tbody>
                          {complianceResults.map((r) => (
                            <tr key={`${r.doc_type}-${r.icv}`} className="border-t">
                              <td className="px-3 py-2 font-mono">{r.doc_type}</td>
                              <td className="px-3 py-2">{r.icv}</td>
                              <td className="px-3 py-2">
                                {['PASS','ALREADY_DONE'].includes(r.status) ? <CheckCircle2 size={14} className="inline text-emerald-600"/> : <XCircle size={14} className="inline text-rose-600"/>} {r.status}
                              </td>
                              <td className="px-3 py-2 text-muted-foreground truncate max-w-xs" title={r.detail}>{r.detail || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Step 4 — Production CSID */}
            {dev?.has_compliance_csid && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    Step 4 — Issue Production CSID
                    {dev?.has_production_csid && <Badge variant="outline" className="ml-2 border-emerald-300 text-emerald-700 text-[10px]">✓ Done</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground mb-3">Once compliance passes, request the final Production CSID. This is the certificate that signs every real invoice for the next ~5 years.</p>
                  <Button onClick={requestProductionCsid} disabled={!canRequestProduction || busy==='pcsid'} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="zatca-pcsid-btn">
                    {busy==='pcsid' ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                    Issue Production CSID
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Help footer */}
        <Card className="border-dashed">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Info size={12}/> How the pipeline works</CardTitle></CardHeader>
          <CardContent className="text-xs text-muted-foreground space-y-1.5">
            <p><b>1. CSR generation</b> — Smarto&apos;s ZATCA SDK (238-R3.4.5) creates an EC keypair + PKCS#10 CSR on the server. Private key never leaves the tenant.</p>
            <p><b>2. Compliance CSID</b> — CSR + your OTP are POSTed to ZATCA&apos;s compliance endpoint; they return a compliance certificate (CCSID).</p>
            <p><b>3. Compliance checks</b> — 6 test invoices (standard/simplified × invoice/credit/debit) are UBL-built, signed and submitted; all must pass.</p>
            <p><b>4. Production CSID</b> — ZATCA issues the long-lived Production CSID (PCSID). Every POS checkout auto-submits: B2C via Reporting API, B2B via Clearance API.</p>
          </CardContent>
        </Card>

        {/* Official ZATCA links & onboarding help */}
        <Card className="border-dashed" data-testid="zatca-official-links-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ExternalLink size={12}/> Official ZATCA Links &amp; Onboarding Help</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-2">
              {[
                { href: 'https://fatoora.zatca.gov.sa/', label: 'FATOORA Portal', sub: 'Generate OTP / Manage Devices', testid: 'zatca-link-fatoora' },
                { href: 'https://zatca.gov.sa/en/eServices/Pages/TaxpayerLookup.aspx', label: 'Verify VAT Registration', sub: 'Official taxpayer lookup', testid: 'zatca-link-vat-lookup' },
                { href: 'https://login.zatca.gov.sa/irj/portal?login=angular&ume.logon.locale=en', label: 'ZATCA Taxpayer Account', sub: 'Company Details / Certificates', testid: 'zatca-link-taxpayer' },
                { href: 'https://www.zatca.gov.sa/en/E-Invoicing/Pages/default.aspx', label: 'Official E-Invoicing Guide', sub: 'Rules & documentation', testid: 'zatca-link-einvoicing-guide' },
              ].map(l => (
                <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer"
                   className="flex items-start gap-2 rounded-lg border bg-muted/30 hover:bg-muted/60 transition-colors px-3 py-2"
                   data-testid={l.testid}>
                  <ExternalLink size={13} className="text-info mt-0.5 shrink-0" />
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold leading-tight">{l.label}</span>
                    <span className="block text-[11px] text-muted-foreground truncate">{l.sub}</span>
                  </span>
                </a>
              ))}
            </div>
            <div className="text-[11px] text-muted-foreground space-y-1 border-t pt-2">
              <p className="flex items-start gap-1.5"><KeyRound size={11} className="mt-0.5 shrink-0 text-amber-600" /> The <b>OTP</b> required for onboarding is obtained only from the official FATOORA portal (Onboard New EGS). Smarto never stores or displays taxpayer credentials or OTPs.</p>
              <p className="flex items-start gap-1.5"><Info size={11} className="mt-0.5 shrink-0" /> Official VAT verification confirms <b>registration status only</b> — it does <b>not</b> confirm that every invoice was successfully reported or cleared. Per-invoice reporting/clearance status is shown on Sales, Invoices and the live banner above.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

function StatTile({ label, value, tone, testid }) {
  const tones = {
    emerald: 'from-emerald-50 to-emerald-100 text-emerald-700 border-emerald-200',
    teal: 'from-teal-50 to-teal-100 text-teal-700 border-teal-200',
    amber: 'from-amber-50 to-amber-100 text-amber-700 border-amber-200',
    rose: 'from-rose-50 to-rose-100 text-rose-700 border-rose-200',
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br ${tones[tone] || tones.emerald} px-4 py-3`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
